import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { TenantDetail } from '@sellokart/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantStatusBadge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MetaItem } from '@/components/MetaItem';
import { IconArrowLeft, IconBan, IconEye, IconPencil } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { ErrorState, Skeleton } from '@/components/States';
import { useIsOwner } from '@/auth/role';
import { useStartImpersonation } from '@/features/impersonation/useImpersonation';
import { PlanUsageSection } from '@/features/tenants/PlanUsageSection';
import { BranchesSection } from '@/features/tenants/BranchesSection';
import { DevicesSection } from '@/features/tenants/DevicesSection';
import { TenantLogoSection } from '@/features/tenants/TenantLogoSection';
import { UsersSection } from '@/features/tenants/UsersSection';
import { fetchTenant, tenantKeys, updateTenant } from '@/features/tenants/api';
import { formatDateTime } from '@/lib/format';

function BackLink() {
  return (
    <Link
      to="/cp"
      className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
    >
      <IconArrowLeft className="h-4 w-4" />
      Tenants
    </Link>
  );
}

export function TenantDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);

  // Status is its own confirmed action rather than a field on the edit form:
  // deactivating signs out every portal user at the shop, which is not
  // something a Save button should do as a side effect of renaming it.
  const statusMutation = useMutation({
    mutationFn: (status: 'ACTIVE' | 'DISABLED') => updateTenant(id, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      setStatusConfirmOpen(false);
    },
  });
  const impersonation = useStartImpersonation();
  // An ISSUER sees the tenant, its branches and tills, and can add a till —
  // every other section and action is an owner's (the API refuses them).
  const isOwner = useIsOwner();

  const { data, isPending, isFetching, isError, error, refetch } = useQuery({
    queryKey: tenantKeys.detail(id),
    queryFn: ({ signal }) => fetchTenant(id, signal),
    enabled: id !== '',
  });

  if (isPending) {
    return (
      <>
        <PageHeader eyebrow={<BackLink />} title="Loading…" />
        <div className="space-y-6">
          <div className="card p-5">
            <Skeleton className="h-4 w-48" />
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <Skeleton className="h-2.5 w-16" />
                  <Skeleton className="h-3.5 w-28" />
                </div>
              ))}
            </div>
          </div>
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="card p-5">
              <Skeleton className="h-4 w-32" />
              <div className="mt-4 space-y-3">
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-5/6" />
                <Skeleton className="h-3.5 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <PageHeader eyebrow={<BackLink />} title="Tenant" />
        <div className="card">
          <ErrorState
            error={error}
            title="Could not load this tenant"
            onRetry={() => void refetch()}
            retrying={isFetching}
          />
        </div>
      </>
    );
  }

  const tenant: TenantDetail = data;
  // The server views as the tenant's oldest ACTIVE user; `users` arrives ordered
  // by creation, so this is the same choice — the dialog must name the account
  // the operator will actually be looking through.
  const viewAsUser = tenant.users.find((user) => user.isActive);

  return (
    <>
      <PageHeader
        eyebrow={<BackLink />}
        title={tenant.name}
        meta={<TenantStatusBadge status={tenant.status} />}
        description={tenant.address || undefined}
        actions={
          isOwner ? (
          <>
            <Button
              icon={<IconEye className="h-4 w-4" />}
              onClick={() => setImpersonateOpen(true)}
              disabled={tenant.status !== 'ACTIVE' || viewAsUser === undefined}
              title={
                tenant.status !== 'ACTIVE'
                  ? 'Re-enable this tenant to view as it'
                  : viewAsUser === undefined
                    ? 'This tenant has no active portal user to view as'
                    : undefined
              }
            >
              View as tenant
            </Button>
            <Button
              icon={<IconPencil className="h-4 w-4" />}
              onClick={() => navigate(`/cp/tenants/${id}/edit`)}
            >
              Edit tenant
            </Button>
            {tenant.status === 'ACTIVE' ? (
              <Button
                icon={<IconBan className="h-4 w-4" />}
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => setStatusConfirmOpen(true)}
                title="Blocks portal sign-in for every user at this shop. Does not affect till sync or licensing."
              >
                Deactivate tenant
              </Button>
            ) : (
              <Button
                icon={<IconBan className="h-4 w-4" />}
                onClick={() => setStatusConfirmOpen(true)}
              >
                Reactivate tenant
              </Button>
            )}
          </>
          ) : undefined
        }
      />

      <div className="space-y-6">
        <div className="card px-5 py-5">
          <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
            <MetaItem label="Timezone">{tenant.timezone}</MetaItem>
            <MetaItem label="Currency">{tenant.currency}</MetaItem>
            <MetaItem label="Phone">{tenant.phone || <span className="text-slate-400">—</span>}</MetaItem>
            <MetaItem label="Registered">{formatDateTime(tenant.createdAt)}</MetaItem>

            {/* Reference only — nothing is typed from it any more. It used to be
                copied into the POS's Cloud Sync settings, and had the boxed,
                copyable treatment that implied; that field went when activation
                codes replaced manual pairing, so the id is now just something
                support quotes. Kept quiet rather than removed, because it is
                still what identifies this tenant in logs. */}
            {/* Spans the row: a uuid wrapped across two lines is hard to read
                back over the phone, which is the only thing it is used for. */}
            <MetaItem label="Tenant ID" className="sm:col-span-2 lg:col-span-4">
              <span className="break-all font-mono text-[13px] font-medium text-slate-600">
                {tenant.id}
              </span>
            </MetaItem>
          </dl>
        </div>

        {isOwner ? (
          <>
            <PlanUsageSection tenantId={tenant.id} />
            <TenantLogoSection tenant={tenant} />
          </>
        ) : null}
        <BranchesSection tenant={tenant} readOnly={!isOwner} />
        <DevicesSection tenant={tenant} />
        {isOwner ? <UsersSection tenant={tenant} /> : null}
      </div>

      <ConfirmDialog
        open={impersonateOpen}
        title={`View as ${tenant.name}?`}
        confirmLabel="Open tenant portal"
        confirmVariant="primary"
        pending={impersonation.isPending}
        error={impersonation.error}
        onConfirm={() => impersonation.mutate(tenant.id)}
        onClose={() => {
          setImpersonateOpen(false);
          impersonation.reset();
        }}
      >
        <p>
          You will see this shop's portal exactly as its owner does, signed in as{' '}
          <strong className="font-medium text-slate-900">{viewAsUser?.username}</strong>.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="font-medium text-slate-900">Read-only.</strong> The tenant portal has
            no way to change anything.
          </li>
          <li>
            <strong className="font-medium text-slate-900">Time-limited.</strong> The session expires
            by itself and cannot be renewed or reloaded.
          </li>
          <li>
            <strong className="font-medium text-slate-900">Audited.</strong> This is recorded against
            your account in the audit log.
          </li>
        </ul>
        <p>Your super-admin session stays signed in — leaving the view returns you here.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={statusConfirmOpen}
        title={
          tenant.status === 'ACTIVE'
            ? `Deactivate ${tenant.name}?`
            : `Reactivate ${tenant.name}?`
        }
        confirmLabel={
          tenant.status === 'ACTIVE' ? 'Deactivate tenant' : 'Reactivate tenant'
        }
        confirmVariant={tenant.status === 'ACTIVE' ? 'danger' : 'primary'}
        pending={statusMutation.isPending}
        error={statusMutation.isError ? statusMutation.error : undefined}
        onConfirm={() =>
          statusMutation.mutate(
            tenant.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
          )
        }
        onClose={() => {
          setStatusConfirmOpen(false);
          statusMutation.reset();
        }}
      >
        {tenant.status === 'ACTIVE' ? (
          <>
            <p className="mb-3">
              Every portal user at{' '}
              <span className="font-medium text-slate-900">{tenant.name}</span>{' '}
              will be signed out and unable to sign back in.
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong className="font-medium text-slate-900">Data is kept.</strong>{' '}
                Branches, tills, sales and history are untouched — this can be
                reversed at any time.
              </li>
              <li>
                <strong className="font-medium text-slate-900">
                  Tills keep trading.
                </strong>{' '}
                This blocks the reports portal only; it does not stop sync or
                revoke any licence.
              </li>
            </ul>
          </>
        ) : (
          <p>
            Portal users at{' '}
            <span className="font-medium text-slate-900">{tenant.name}</span> will
            be able to sign in again straight away.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
